import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Link,
  Alert,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useIntl } from "react-intl";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { auth } from "../../config/firebaseConfig";
import { checkCompany } from "../../api/companyApi";
import { resolveBackendErrorKey } from "../../utils/backendErrors";

// Firebase エラーコード → i18n キー
const firebaseErrorMessages = {
  "auth/email-already-in-use": "auth_email_already_in_use",
  "auth/invalid-email": "auth_invalid_email",
  "auth/weak-password": "auth_weak_password",
  "auth/invalid-credential": "auth_invalid_credential",
  "auth/network-request-failed": "auth_network_request_failed",
  "auth/too-many-requests": "auth_too_many_requests",
  "auth/unauthorized-domain": "auth_unauthorized_domain",
  "auth/unauthorized-continue-uri": "auth_unauthorized_domain",
  "auth/operation-not-allowed": "auth_operation_not_allowed",
  "auth/internal-error": "auth_internal_error",
};

const SIGNUP_STORAGE_KEY = "pendingSignUp";

export default function SignUpPage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const [form, setForm] = useState({
    companyName: "",
    companyDomainName: "",
    companyPassword: "",
    eaName: "",
    eaPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    try {
      // 1. バックエンドで企業名・ドメインの重複チェック
      await checkCompany({
        companyName: form.companyName,
        companyDomainName: form.companyDomainName,
        companyPassword: form.companyPassword,
        eaName: form.eaName,
        eaPassword: form.eaPassword,
      });

      // 2. Firebase でユーザー作成
      const credential = await createUserWithEmailAndPassword(
        auth,
        form.eaName,
        form.eaPassword
      );

      // 3. サインアップデータを localStorage に保存（メール確認後に使用）
      localStorage.setItem(
        SIGNUP_STORAGE_KEY,
        JSON.stringify({
          companyName: form.companyName,
          companyDomainName: form.companyDomainName,
          companyPassword: form.companyPassword,
          eaName: form.eaName,
          eaPassword: form.eaPassword,
        })
      );

      // 4. 確認メール送信（アプリ内の /verify-email へリダイレクトするよう設定）
      const actionCodeSettings = {
        url: `${window.location.origin}/verify-email`,
        handleCodeInApp: true,
      };
      await sendEmailVerification(credential.user, actionCodeSettings);
      setEmailSent(true);
    } catch (err) {
      const firebaseKey = firebaseErrorMessages[err.code];
      if (firebaseKey) {
        setErrorMsg(t(firebaseKey));
      } else {
        setErrorMsg(t(resolveBackendErrorKey(err.message, "auth.error.signin")));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          mt: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: "secondary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1,
          }}
        >
          <LockOutlinedIcon sx={{ color: "white" }} />
        </Box>
        <Typography component="h1" variant="h5" gutterBottom>
          {t("auth.signup.title")}
        </Typography>

        <Paper sx={{ p: 4, width: "100%", mt: 2 }}>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}
          {emailSent ? (
            <Alert severity="success">{t("auth.signup.emailSent")}</Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label={t("auth.company.name")}
                name="companyName"
                value={form.companyName}
                onChange={handleChange}
                fullWidth
                required
                autoFocus
                margin="normal"
              />
              <TextField
                label={t("auth.company.domainName")}
                name="companyDomainName"
                value={form.companyDomainName}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label={t("auth.company.password")}
                name="companyPassword"
                type="password"
                value={form.companyPassword}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label={t("auth.ea.email")}
                name="eaName"
                type="email"
                value={form.eaName}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label={t("auth.ea.password")}
                name="eaPassword"
                type="password"
                value={form.eaPassword}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, mb: 2 }}
              >
                {t("auth.signup.title")}
              </Button>
            </Box>
          )}
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Link component={RouterLink} to="/signin" variant="body2">
              {t("auth.signin.link")}
            </Link>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
